import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const POSCheckout = ({ cart, onUpdateQuantity, onRemoveItem, onClearCart, onCheckout, creditCardConfigs }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cardLast4, setCardLast4] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stockWarnings, setStockWarnings] = useState([]);

  const subtotal = cart?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) || 0;
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + tax;

  // Check for stock issues
  React.useEffect(() => {
    const warnings = [];
    cart?.forEach(item => {
      if (item?.stock_quantity !== undefined) {
        if (item?.quantity > item?.stock_quantity) {
          warnings?.push({
            productId: item?.id,
            productName: item?.name,
            requested: item?.quantity,
            available: item?.stock_quantity,
            type: 'insufficient'
          });
        } else if (item?.stock_quantity <= item?.low_stock_threshold) {
          warnings?.push({
            productId: item?.id,
            productName: item?.name,
            available: item?.stock_quantity,
            type: 'low'
          });
        }
      }
    });
    setStockWarnings(warnings);
  }, [cart]);

  const hasInsufficientStock = stockWarnings?.some(w => w?.type === 'insufficient');

  const handleCheckout = async () => {
    if (cart?.length === 0) return;

    if (hasInsufficientStock) {
      alert('Cannot complete checkout: Some items exceed available stock');
      return;
    }

    if (paymentMethod === 'credit_card' && !cardLast4?.trim()) {
      alert('Please enter the last 4 digits of the credit card');
      return;
    }

    setProcessing(true);
    const { error } = await onCheckout({
      paymentMethod,
      cardLast4: paymentMethod === 'credit_card' ? cardLast4 : null
    });

    if (error) {
      alert(error?.message || 'Transaction failed');
    } else {
      alert('Transaction completed successfully! Inventory updated.');
      setCardLast4('');
      setStockWarnings([]);
    }
    setProcessing(false);
  };

  const hasEnabledCreditCard = creditCardConfigs?.length > 0;

  return (
    <div className="bg-card rounded-xl shadow-md border border-border sticky top-6">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Checkout</h2>
          {cart?.length > 0 && (
            <button
              onClick={onClearCart}
              className="text-xs text-error hover:text-error/80 transition-colors"
            >
              Clear Cart
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        {cart?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ShoppingCart" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Cart is empty</p>
          </div>
        ) : (
          <>
            {/* Stock Warnings */}
            {stockWarnings?.length > 0 && (
              <div className="mb-4 space-y-2">
                {stockWarnings?.map((warning, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      warning?.type === 'insufficient' ?'bg-error/10 border-error' :'bg-warning/10 border-warning'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon 
                        name={warning?.type === 'insufficient' ? 'XCircle' : 'AlertTriangle'} 
                        size={16} 
                        color={warning?.type === 'insufficient' ? 'var(--color-error)' : 'var(--color-warning)'} 
                        className="mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">
                          {warning?.productName}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {warning?.type === 'insufficient' 
                            ? `Requested: ${warning?.requested}, Available: ${warning?.available}` 
                            : `Low stock: ${warning?.available} remaining`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cart Items */}
            <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
              {cart?.map(item => {
                const hasStockIssue = stockWarnings?.find(w => w?.productId === item?.id && w?.type === 'insufficient');
                
                return (
                  <div 
                    key={item?.id} 
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      hasStockIssue ? 'bg-error/5 border border-error' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground mb-1 line-clamp-1">
                        {item?.name}
                      </p>
                      <p className="text-xs text-muted-foreground mb-1">
                        ${item?.price?.toFixed(2)} each
                      </p>
                      {item?.stock_quantity !== undefined && (
                        <p className="text-xs text-muted-foreground mb-2">
                          Available: {item?.stock_quantity}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateQuantity(item?.id, item?.quantity - 1)}
                          className="w-6 h-6 rounded bg-muted hover:bg-muted/80 flex items-center justify-center"
                        >
                          <Icon name="Minus" size={14} />
                        </button>
                        <span className="text-sm font-medium text-foreground w-8 text-center data-text">
                          {item?.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item?.id, item?.quantity + 1)}
                          disabled={item?.stock_quantity !== undefined && item?.quantity >= item?.stock_quantity}
                          className="w-6 h-6 rounded bg-muted hover:bg-muted/80 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Plus" size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground mb-2 data-text">
                        ${(item?.price * item?.quantity)?.toFixed(2)}
                      </p>
                      <button
                        onClick={() => onRemoveItem(item?.id)}
                        className="p-1 rounded hover:bg-error/10 transition-colors"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-error)" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="space-y-2 mb-4 pb-4 border-b border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium text-foreground data-text">${subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tax (8%)</span>
                <span className="font-medium text-foreground data-text">${tax?.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-semibold text-foreground">Total</span>
                <span className="text-xl font-bold text-primary data-text">${total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-foreground">
                Payment Method
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setPaymentMethod('cash')}
                  className={`
                    p-3 rounded-lg border-2 transition-all
                    ${paymentMethod === 'cash' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }
                  `}
                >
                  <Icon name="Banknote" size={20} color={paymentMethod === 'cash' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} className="mx-auto mb-1" />
                  <p className="text-xs font-medium text-foreground">Cash</p>
                </button>
                <button
                  onClick={() => setPaymentMethod('credit_card')}
                  disabled={!hasEnabledCreditCard}
                  className={`
                    p-3 rounded-lg border-2 transition-all
                    ${paymentMethod === 'credit_card' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }
                    ${!hasEnabledCreditCard ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <Icon name="CreditCard" size={20} color={paymentMethod === 'credit_card' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} className="mx-auto mb-1" />
                  <p className="text-xs font-medium text-foreground">Card</p>
                </button>
              </div>
              {!hasEnabledCreditCard && (
                <p className="text-xs text-warning flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />
                  Credit card payments not configured
                </p>
              )}
            </div>

            {/* Credit Card Input */}
            {paymentMethod === 'credit_card' && (
              <div className="mb-4">
                <Input
                  label="Last 4 Digits of Card"
                  type="text"
                  placeholder="1234"
                  value={cardLast4}
                  onChange={(e) => setCardLast4(e?.target?.value?.replace(/\D/g, '')?.slice(0, 4))}
                  maxLength={4}
                />
              </div>
            )}

            {/* Checkout Button */}
            <Button
              fullWidth
              onClick={handleCheckout}
              disabled={processing || hasInsufficientStock || (paymentMethod === 'credit_card' && !cardLast4?.trim())}
              iconName="CreditCard"
              iconPosition="left"
            >
              {processing ? 'Processing...' : hasInsufficientStock ? 'Insufficient Stock' : `Complete Payment - $${total?.toFixed(2)}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default POSCheckout;